/**
 * Seiten-Editor des Monats (Board „Monat je Apartment“): Auswahl mit bearbeitbaren Chips und
 * „Weiteren Zeitraum“ über Monate, die Felder, der Satz, was passiert, und „Änderungen prüfen“.
 * Er läuft über denselben Schreibpfad und dieselbe Abbildung wie der Apartment-Reiter
 * (`toBatchRanges` → `ari/batch`, Nachverfolgung der Operationen); fachlich prüft der Server.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AvailabilityDistributionApartment } from '@/shared/api';
import { RangeChipList } from '../calendar/RangeChipList';
import { EMPTY_EDITOR_VALUES, type InlineEditorValues } from '../calendar/inlineEditorModel';
import type { IsoRange } from '../calendar/rangePickerModel';
import type { SelectedRange } from '../calendar/rangeSelectionModel';
import { MonthEditorEmpty, MonthEditorFooter } from './MonthEditorParts';
import { MonthEditorFields } from './MonthEditorFields';
import { MonthReviewDialog } from './MonthReviewDialog';
import { editorPlaceholders, selectionTitle } from './monthEditorModel';
import { reviewDays, valuesSummary } from './monthReview';
import { useMonthPush } from './useMonthPush';

interface Props {
  apartment: AvailabilityDistributionApartment;
  today: string;
  ranges: readonly SelectedRange[];
  onRemoveRange: (id: string) => void;
  onPatchRange: (id: string, range: IsoRange) => void;
  onAddRange: (range: IsoRange) => void;
  onClear: () => void;
  onPushed: () => void;
  /** Die Werte gehören dem Monat: ein geschlossenes Handy-Blatt verliert sie nicht. */
  values: InlineEditorValues;
  onValuesChange: (values: InlineEditorValues) => void;
  /** Preisautomatik der Auswahl unter den Feldern (nur mit Schicht). */
  extra?: ReactNode;
}

export function MonthEditor({ apartment, today, ranges, values, ...props }: Props) {
  const setValues = props.onValuesChange;
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (ranges.length > 0) setDone(false);
  }, [ranges.length]);
  const { review, push, pushError, start, close } = useMonthPush(() => {
    setValues(EMPTY_EDITOR_VALUES);
    setDone(true);
    props.onPushed();
  });
  const placeholders = useMemo(
    () => editorPlaceholders(apartment.days, ranges),
    [apartment.days, ranges],
  );
  if (ranges.length === 0) {
    return (
      <MonthEditorEmpty done={done}>
        {/* Der freie Zeitraum über Monate, ohne erst im Raster zu klicken. */}
        <RangeChipList
          ranges={[]}
          today={today}
          disabled={false}
          onRemove={props.onRemoveRange}
          onEdit={props.onPatchRange}
          onAdd={props.onAddRange}
        />
      </MonthEditorEmpty>
    );
  }
  const title = selectionTitle(ranges);
  const busy = push.isPending;

  return (
    <>
      <div className="border-hairline flex flex-col gap-2.5 border-b p-[22px]">
        <span className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.12em]">
          Auswahl · {apartment.name}
        </span>
        <span className="font-serif text-[22px] font-bold leading-tight">{title}</span>
        <RangeChipList
          ranges={ranges}
          today={today}
          disabled={busy}
          onRemove={props.onRemoveRange}
          onEdit={props.onPatchRange}
          onAdd={props.onAddRange}
        />
      </div>
      <div className="px-[22px] py-[18px]">
        <MonthEditorFields
          values={values}
          onChange={(patch) => setValues({ ...values, ...patch })}
          disabled={busy}
          {...placeholders}
        />
        {props.extra && <div className="mt-3.5">{props.extra}</div>}
      </div>
      <MonthEditorFooter
        apartmentName={apartment.name}
        ranges={ranges}
        availability={values.availability}
        busy={busy}
        onReview={() => start(ranges, values)}
        onDiscard={() => {
          setValues(EMPTY_EDITOR_VALUES);
          props.onClear();
        }}
      />
      <MonthReviewDialog
        open={review !== null}
        propertyId={apartment.propertyId}
        apartmentName={apartment.name}
        title={`${title}: ${valuesSummary(values, apartment.currency)}`}
        ranges={review ?? []}
        days={reviewDays(apartment.days, ranges, {
          rate: review?.[0]?.rate != null,
          minStay: review?.[0]?.minStay != null,
        })}
        saving={busy}
        pushError={pushError}
        onBack={close}
        onConfirm={() =>
          review && push.mutate({ propertyId: apartment.propertyId, ranges: review })
        }
      />
    </>
  );
}
